/**
 * Harbour mutation toasts. Claim races must name the owner — never a silent overwrite.
 * Self-claim is not a 409: already-yours is info, not “someone else”.
 */

import React from 'react'
import { message } from 'antd'
import {
  HARBOUR_ALREADY_YOURS_COPY,
  HARBOUR_NOTES_ADDED,
  HARBOUR_NOTES_ADDED_PINNED,
  HARBOUR_NOTES_PINNED_TOAST,
  HARBOUR_NOTES_UNPINNED_TOAST,
  HARBOUR_OUTCOME,
  getHarbourOutcomeLabel,
  isHarbourOutcome,
} from 'src/constants/harbour'
import { formatHarbourFollowUpLabel } from 'src/utils/harbourMappers'
import { TOAST_DURATION_SEC } from 'src/utils/messageToast'

const HARBOUR_MESSAGE_Z_INDEX = 2000
export const HARBOUR_OFFRAMP_UNDO_TOAST_DURATION_S = 5

function toastHarbourSuccess(content) {
  message.success(content, TOAST_DURATION_SEC)
  if (typeof document === 'undefined') {
    return
  }
  const root = document.querySelector('.ant-message')
  if (root) {
    root.style.zIndex = String(HARBOUR_MESSAGE_Z_INDEX)
  }
}

export function formatHarbourAlreadyTakenMessage(error = {}, venueName = '') {
  const owner = String(error?.ownerName || '').trim()
  const venue = String(error?.venueName || venueName || '').trim()
  const fromApi = String(error?.message || '').trim()

  if (fromApi && owner && fromApi.includes(owner)) {
    return fromApi
  }
  if (owner && venue) {
    return `${owner} is already on ${venue}`
  }
  if (owner) {
    return `${owner} is already on this venue`
  }
  if (fromApi) {
    return fromApi
  }
  return 'Someone else is already on this venue'
}

export function formatHarbourAlreadyYoursMessage(venueName = '') {
  const venue = String(venueName || '').trim()
  if (venue) {
    return `You're already on ${venue}`
  }
  return HARBOUR_ALREADY_YOURS_COPY
}

export function toastHarbourAlreadyYours(venueName) {
  message.info(formatHarbourAlreadyYoursMessage(venueName))
}

export function toastHarbourStartWorkSuccess(result = {}, venueName = '') {
  const name = String(result.venueName || venueName || '').trim()
  if (result.alreadyOwned) {
    toastHarbourAlreadyYours(name)
    return
  }
  message.success(`Started work on ${name || 'this venue'}`)
}

export function toastHarbourStartWorkSkip(skip, venueName) {
  if (!skip) {
    return
  }
  if (skip.alreadyOwned) {
    toastHarbourAlreadyYours(venueName)
    return
  }
  if (skip.alreadyTaken) {
    toastHarbourMutationError(
      { alreadyTaken: true, ownerName: skip.ownerName, venueName },
      { venueName },
    )
  }
}

export function toastHarbourMutationError(error, { venueName } = {}) {
  if (error?.alreadyTaken) {
    message.warning(formatHarbourAlreadyTakenMessage(error, venueName))
    return
  }
  message.error(error?.message || 'Harbour request failed')
}

export function formatHarbourLoggedToast(memory = {}, now = new Date()) {
  const outcomeLabel = isHarbourOutcome(memory.outcome)
    ? getHarbourOutcomeLabel(memory.outcome)
    : ''
  const prefix = outcomeLabel ? `Logged ${outcomeLabel}` : 'Logged'
  if (memory.outcome === HARBOUR_OUTCOME.CLOSED) {
    return prefix
  }
  const when =
    memory.followUpLabel && memory.followUpLabel !== 'No follow-up'
      ? memory.followUpLabel
      : formatHarbourFollowUpLabel(memory.nextFollowUpAt, now) || ''
  if (!when) {
    return prefix
  }
  return `${prefix} · follow-up ${when}`
}

export function toastHarbourLogged(memory, now) {
  message.success(formatHarbourLoggedToast(memory, now))
}

export function formatHarbourNoteAddedToast({ pinned } = {}) {
  return pinned ? HARBOUR_NOTES_ADDED_PINNED : HARBOUR_NOTES_ADDED
}

export function toastHarbourNoteAdded({ pinned } = {}) {
  toastHarbourSuccess(formatHarbourNoteAddedToast({ pinned }))
}

export function formatHarbourNotePinToast(pinned) {
  return pinned ? HARBOUR_NOTES_PINNED_TOAST : HARBOUR_NOTES_UNPINNED_TOAST
}

export function toastHarbourNotePinChanged(pinned) {
  toastHarbourSuccess(formatHarbourNotePinToast(pinned))
}

export function toastHarbourOfframpWithUndo({
  text,
  onUndo,
  testId = 'harbour-offramp-toast',
} = {}) {
  const toastKey = `harbour-offramp-${Date.now()}`
  let used = false

  const handleUndo = async event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    if (used) {
      return
    }
    used = true
    message.destroy(toastKey)
    await onUndo?.()
  }

  message.success({
    key: toastKey,
    duration: HARBOUR_OFFRAMP_UNDO_TOAST_DURATION_S,
    content: (
      <span data-testid={testId}>
        {text}
        {' — '}
        <button
          type='button'
          data-testid='harbour-offramp-undo'
          onClick={handleUndo}
          style={{
            padding: 0,
            border: 0,
            background: 'none',
            color: 'inherit',
            cursor: 'pointer',
            textDecoration: 'underline',
            font: 'inherit',
          }}
        >
          Undo
        </button>
      </span>
    ),
  })
}
